import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBook, FiFilter, FiAlertCircle, FiCheckCircle, FiInfo, FiCircle, FiChevronDown, FiClock, FiArrowRight, FiSearch, FiLayers, FiGrid } = FiIcons;

// Helper component for highlighting text
const HighlightText = ({ text, highlight }) => {
  if (!text) return null;
  if (!highlight || !highlight.trim()) return text;

  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  try {
    const regex = new RegExp(`(${escapeRegExp(highlight)})`, 'gi');
    const parts = text.toString().split(regex);

    return (
      <span>
        {parts.map((part, i) => 
          regex.test(part) ? (
            <span key={i} className="bg-yellow-200 text-gray-900 font-medium px-0.5 rounded-sm shadow-sm border border-yellow-300/50">
              {part}
            </span>
          ) : (
            part
          )
        )}
      </span>
    );
  } catch (e) {
    return text;
  }
};

const StatusSelector = ({ status, isTeamOwner, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const options = [
    { value: 0, label: 'OFFEN', icon: FiCircle, color: 'text-gray-500', bg: 'bg-gray-100', border: 'border-gray-200' },
    { value: 1, label: 'IN PROGRESS', icon: FiClock, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
    { value: 2, label: 'UMGESETZT', icon: FiCheckCircle, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' }
  ];

  const currentOption = options.find(o => 
    (status === 'open' && o.value === 0) ||
    (status === 'in_progress' && o.value === 1) ||
    (status === 'implemented' && o.value === 2)
  ) || options[0];

  if (!isTeamOwner) {
    return (
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold tracking-wide border ${currentOption.bg} ${currentOption.color} ${currentOption.border} cursor-default select-none`}>
        <SafeIcon icon={currentOption.icon} className="w-4 h-4" />
        {currentOption.label}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold tracking-wide border transition-all ${currentOption.bg} ${currentOption.color} ${currentOption.border} hover:shadow-sm`}
      >
        <SafeIcon icon={currentOption.icon} className="w-4 h-4" />
        {currentOption.label}
        <SafeIcon icon={FiChevronDown} className={`w-3 h-3 ml-1 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-44 bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-30 animate-fadeIn">
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={(e) => {
                e.stopPropagation();
                onChange(opt.value);
                setIsOpen(false);
              }}
              className={`w-full text-left px-4 py-2.5 text-xs font-semibold flex items-center gap-3 transition-colors ${
                currentOption.value === opt.value 
                  ? 'bg-brand-primary/5 text-brand-secondary' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <SafeIcon icon={opt.icon} className={`w-4 h-4 ${opt.color}`} />
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const RecommendationItem = ({ rec, isTeamOwner, onStatusChange, searchTerm }) => {
  const navigate = useNavigate();

  const handleDetailsClick = () => {
    navigate(`/handbook/recommendation/${rec.id}`);
  };

  return (
    <div className={`bg-white p-5 rounded-lg border shadow-sm transition-all ${
      rec.status === 'implemented' ? 'border-green-200 bg-green-50/30' : 
      rec.status === 'in_progress' ? 'border-blue-200 bg-blue-50/20' :
      'border-gray-200 hover:border-brand-primary/50'
    }`}>
      <div className="flex justify-between items-start gap-6">
        <div className="flex-1">
          <h5 className={`text-base font-bold mb-2 ${
            rec.status === 'implemented' ? 'text-green-800' : 
            rec.status === 'in_progress' ? 'text-blue-800' :
            'text-brand-dark'
          }`}>
            <HighlightText text={rec.title} highlight={searchTerm} />
          </h5>
          <div className="text-sm text-gray-600">
            <HighlightText text={rec.short_description || 'Keine Kurzbeschreibung verfügbar.'} highlight={searchTerm} />
          </div>
        </div>
        
        <div className="flex items-center gap-4 shrink-0">
          <button 
            onClick={handleDetailsClick}
            className="text-sm font-semibold text-brand-secondary hover:text-brand-dark flex items-center transition-colors group"
          >
            Details ansehen
            <SafeIcon icon={FiArrowRight} className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
          <StatusSelector 
            status={rec.status} 
            isTeamOwner={isTeamOwner} 
            onChange={(newStatus) => onStatusChange(rec.id, newStatus, rec.relationId)} 
          />
        </div>
      </div>
    </div>
  );
};

const Handbook = () => {
  const { user, isTeamOwner } = useAuth();
  const [loading, setLoading] = useState(true);
  const [elements, setElements] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [selectedClusterId, setSelectedClusterId] = useState('all');
  const [selectedElementId, setSelectedElementId] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [analyzedQuestions, setAnalyzedQuestions] = useState([]);

  useEffect(() => {
    if (user?.teamId) {
      loadHandbookData();
    }
  }, [user]);

  const loadHandbookData = async () => {
    try {
      setLoading(true);

      const [
        teamUsesResponse,
        allElementsResponse,
        allQuestionsResponse,
        teamMembersResponse,
        recommendationsResponse,
        recRelationsResponse,
        teamUsedRecsResponse,
        clustersResponse,
        clusterRelationsResponse
      ] = await Promise.all([
        apiService.getTeamUsesElement({ team_id: user.teamId, is_used: 1, limit: 1000 }),
        apiService.getElements({ limit: 1000 }),
        apiService.getQuestions({ limit: 1000 }),
        apiService.getUserTeamRelations({ team_id: user.teamId, limit: 1000 }),
        apiService.getRecommendations({ limit: 1000 }),
        apiService.getQuestionRecommendationRelations({ limit: 1000 }),
        apiService.getTeamUsedRecommendations({ team_id: user.teamId, limit: 1000 }),
        apiService.getClusters({ limit: 1000 }),
        apiService.getClusterElementRelations({ limit: 1000 })
      ]);
      
      const activeElementIds = (teamUsesResponse.data || []).map(r => r.element_id);
      const allClusters = clustersResponse.data || [];
      const allClusterRelations = clusterRelationsResponse.data || [];
      
      setClusters(allClusters);

      if (activeElementIds.length === 0) {
        setElements([]);
        setAnalyzedQuestions([]);
        setLoading(false);
        return;
      }

      const activeElements = (allElementsResponse.data || []).filter(el => 
        activeElementIds.includes(el.id) || activeElementIds.includes(el.id.toString())
      );
      
      const teamMemberIds = (teamMembersResponse.data || []).map(m => m.user_id);
      if (user.id && !teamMemberIds.includes(parseInt(user.id))) {
        teamMemberIds.push(parseInt(user.id));
      }

      const evaluationResponses = await Promise.all(
        teamMemberIds.map(uid => apiService.getEvaluations({ user_id: uid, limit: 1000 }))
      );
      const allEvaluations = evaluationResponses.flatMap(res => res.data || []);

      const allRecommendations = recommendationsResponse.data || [];
      const allRecRelations = recRelationsResponse.data || [];
      const teamUsedRecs = teamUsedRecsResponse.data || [];
      const allQuestions = allQuestionsResponse.data || [];

      const processedQuestions = [];
      const elementsWithScores = [];

      for (const element of activeElements) {
        const elementQuestions = allQuestions.filter(q => q.element_id === element.id);
        
        // Find Cluster for this element
        const clusterRelation = allClusterRelations.find(r => r.element_id === element.id);
        const clusterId = clusterRelation ? clusterRelation.cluster_id : null;

        let elementTotalValue = 0;
        let elementTotalCount = 0;

        for (const question of elementQuestions) {
          const relevantEvaluations = allEvaluations.filter(ev => 
            ev.question_id === question.id
          );

          let averageScore = 0;
          if (relevantEvaluations.length > 0) {
            const sum = relevantEvaluations.reduce((acc, curr) => acc + curr.evaluation_value, 0);
            averageScore = sum / relevantEvaluations.length;
            
            elementTotalValue += sum;
            elementTotalCount += relevantEvaluations.length;
          }

          const linkedRecRelations = allRecRelations.filter(r => 
            r.question_id === question.id
          );
          
          const linkedRecommendations = linkedRecRelations.map(rel => {
            const rec = allRecommendations.find(rec => 
              rec.id == rel.recommendation_id || rec.id == rel.recommendation
            );
            
            if (!rec) return null;

            const usage = teamUsedRecs.find(u => 
              (u.recommendation_id == rec.id) && 
              (u.team_id == user.teamId)
            );

            let status = 'open';
            if (usage) {
              if (usage.in_progress_used !== undefined && usage.in_progress_used !== null) {
                  const val = String(usage.in_progress_used);
                  if (val === '1') status = 'in_progress';
                  else if (val === '2') status = 'implemented';
                  else status = 'open';
              } else {
                  status = usage.is_used === 1 ? 'implemented' : 'open';
              }
            }

            return {
              ...rec,
              status: status,
              relationId: usage ? usage.id : null
            };
          }).filter(Boolean);

          // Threshold for "not perfect" changed from 10 to 7
          if (linkedRecommendations.length > 0 || averageScore < 7) {
             processedQuestions.push({
               ...question,
               elementTitle: element.title,
               clusterId: clusterId, // Add cluster ID for filtering
               averageScore: averageScore,
               evaluationCount: relevantEvaluations.length,
               recommendations: linkedRecommendations
             });
          }
        }

        const elementAvg = elementTotalCount > 0 
          ? (elementTotalValue / elementTotalCount).toFixed(1) 
          : null;
        
        elementsWithScores.push({
          ...element,
          clusterId: clusterId, // Add cluster ID for filtering
          averageScore: elementAvg
        });
      }

      setElements(elementsWithScores);

      // Sort by score (lowest first), use 8 as "perfect/no data" value for sorting (max is 7 now)
      processedQuestions.sort((a, b) => {
        const scoreA = a.evaluationCount > 0 ? a.averageScore : 8;
        const scoreB = b.evaluationCount > 0 ? b.averageScore : 8;
        return scoreA - scoreB;
      });

      setAnalyzedQuestions(processedQuestions);

    } catch (error) {
      console.error("Error loading handbook data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (recId, newStatusValue, relationId) => {
    // 1. Determine old status
    let oldStatusValue = '0';
    for (const q of analyzedQuestions) {
      const rec = q.recommendations.find(r => r.id === recId);
      if (rec) {
        if (rec.status === 'in_progress') oldStatusValue = '1';
        if (rec.status === 'implemented') oldStatusValue = '2';
        break;
      }
    }

    const statusMap = { 0: 'open', 1: 'in_progress', 2: 'implemented' };
    const newStatusStr = statusMap[newStatusValue];
    
    // Optimistic Update
    setAnalyzedQuestions(prev => prev.map(q => ({
      ...q,
      recommendations: q.recommendations.map(r => {
        if (r.id === recId) {
          return { ...r, status: newStatusStr };
        }
        return r;
      })
    })));

    // 2. Log status change if different
    if (oldStatusValue !== newStatusValue.toString()) {
      const statusChangePayload = {
        recommendation_id: recId,
        status_change_start: oldStatusValue,
        status_change_end: newStatusValue.toString(),
        datetime: new Date().toISOString().slice(0, 19).replace('T', ' ')
      };
      
      // Fire and forget log creation
      apiService.createRecommendationStatusChange(statusChangePayload)
        .catch(err => console.error("Failed to log status change", err));
    }

    // 3. Update team_used_recommendation
    const newIsUsed = newStatusValue > 0 ? 1 : 0;
    try {
      const payload = {
        in_progress_used: newStatusValue.toString(),
        is_used: newIsUsed,
        recommendation_id: recId,
        team_id: user.teamId
      };

      if (relationId) {
        await apiService.updateTeamUsedRecommendation(relationId, payload);
      } else {
        const res = await apiService.createTeamUsedRecommendation(payload);
        
        if (res && (res.id || res.data?.id)) {
           const newId = res.id || res.data?.id;
           setAnalyzedQuestions(prev => prev.map(q => ({
            ...q,
            recommendations: q.recommendations.map(r => {
              if (r.id === recId) {
                return { ...r, relationId: newId };
              }
              return r;
            })
          })));
        }
      }
    } catch (error) {
      console.error("Error updating recommendation status:", error);
      loadHandbookData(); // Revert on error
    }
  };

  // Filter Logic
  const filteredElements = useMemo(() => {
    if (selectedClusterId === 'all') return elements;
    return elements.filter(el => el.clusterId === parseInt(selectedClusterId));
  }, [elements, selectedClusterId]);

  const filteredQuestions = useMemo(() => {
    let result = analyzedQuestions;

    // 1. Filter by Cluster
    if (selectedClusterId !== 'all') {
      result = result.filter(q => q.clusterId === parseInt(selectedClusterId));
    }

    // 2. Filter by Element
    if (selectedElementId !== 'all') {
      result = result.filter(q => q.element_id === parseInt(selectedElementId));
    }

    // 3. Filter by Search Text
    if (searchTerm.trim() !== '') {
      const lowerTerm = searchTerm.toLowerCase();
      result = result.filter(q => {
        // Search in Question text
        if (q.text && q.text.toLowerCase().includes(lowerTerm)) return true;
        
        // Search in Recommendations
        return q.recommendations.some(rec => 
          (rec.title && rec.title.toLowerCase().includes(lowerTerm)) ||
          (rec.short_description && rec.short_description.toLowerCase().includes(lowerTerm))
        );
      });
    }

    return result;
  }, [selectedClusterId, selectedElementId, searchTerm, analyzedQuestions]);

  // Reset selected element when cluster changes
  useEffect(() => {
    if (selectedClusterId !== 'all' && selectedElementId !== 'all') {
      const element = elements.find(e => e.id === parseInt(selectedElementId));
      if (element && element.clusterId !== parseInt(selectedClusterId)) {
        setSelectedElementId('all');
      }
    }
  }, [selectedClusterId]);

  // Updated color thresholds: >= 6 (green), >= 4 (yellow), else red
  const getScoreColor = (score, count) => {
    if (count === 0) return 'bg-gray-100 text-gray-500 border-gray-200';
    if (score >= 6) return 'bg-green-50 text-green-700 border-green-200';
    if (score >= 4) return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    return 'bg-red-50 text-red-700 border-red-200';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="w-full relative min-h-screen">
      <div className="w-full p-6">
        <div className="mb-6 flex-shrink-0">
          <h1 className="text-2xl font-bold text-brand-dark flex items-center">
            <SafeIcon icon={FiBook} className="w-7 h-7 mr-3 text-brand-secondary" />
            Team Handbuch
          </h1>
          <p className="text-gray-600 mt-2">
            Hier finden Sie gezielte Handlungsempfehlungen basierend auf den Bereichen mit dem größten Entwicklungspotenzial.
          </p>
        </div>

        {/* Improved Filter & Search Section */}
        <div className="mb-8 bg-gray-50 rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <SafeIcon icon={FiFilter} className="text-brand-secondary" />
            <h3 className="font-semibold text-gray-800">Filter & Suche</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {/* Cluster Select */}
            <div className="md:col-span-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiGrid} className="text-gray-400" />
                </div>
                <select
                  value={selectedClusterId}
                  onChange={(e) => setSelectedClusterId(e.target.value)}
                  className="block w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 focus:ring-brand-primary focus:border-brand-primary appearance-none transition-shadow hover:shadow-sm"
                >
                  <option value="all">Alle Cluster</option>
                  {clusters.map(cluster => (
                    <option key={cluster.id} value={cluster.id}>{cluster.title}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiChevronDown} className="text-gray-400 w-4 h-4" />
                </div>
              </div>
            </div>

            {/* Element Select */}
            <div className="md:col-span-4">
              <div className="relative">
                 <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiLayers} className="text-gray-400" />
                </div>
                <select
                  value={selectedElementId}
                  onChange={(e) => setSelectedElementId(e.target.value)}
                  className="block w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 focus:ring-brand-primary focus:border-brand-primary appearance-none transition-shadow hover:shadow-sm"
                >
                  <option value="all">Alle Elemente {selectedClusterId !== 'all' ? 'im Cluster' : ''}</option>
                  {filteredElements.map(el => (
                    <option key={el.id} value={el.id}>
                      {el.title} ({el.averageScore ? `Ø ${el.averageScore}` : '-'})
                    </option>
                  ))}
                </select>
                 <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiChevronDown} className="text-gray-400 w-4 h-4" />
                </div>
              </div>
            </div>

            {/* Text Search */}
            <div className="md:col-span-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiSearch} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Suche in Fragen & Empfehlungen..."
                  className="block w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:ring-brand-primary focus:border-brand-primary transition-shadow hover:shadow-sm"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {filteredQuestions.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="bg-gray-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                 <SafeIcon icon={FiSearch} className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-brand-dark mb-1">Keine Ergebnisse gefunden</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                Es wurden keine Fragen oder Empfehlungen gefunden, die den aktuellen Filterkriterien entsprechen.
                {searchTerm && <span className="block mt-1">Suchbegriff: <strong>"{searchTerm}"</strong></span>}
              </p>
              <button 
                onClick={() => {
                  setSelectedClusterId('all');
                  setSelectedElementId('all');
                  setSearchTerm('');
                }}
                className="mt-6 px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Filter zurücksetzen
              </button>
            </div>
          ) : (
            filteredQuestions.map((item) => (
              <div key={item.id} className="bg-white rounded-xl shadow-sm border border-gray-200 transition-shadow hover:shadow-md animate-fadeIn">
                <div className="p-6 border-b border-gray-100 rounded-t-xl">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs font-bold uppercase tracking-wider text-brand-secondary">
                          {item.elementTitle}
                        </span>
                        {item.clusterId && clusters.find(c => c.id === item.clusterId) && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-medium border border-gray-200">
                             {clusters.find(c => c.id === item.clusterId).title}
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-brand-dark leading-tight">
                        <HighlightText text={item.text} highlight={searchTerm} />
                      </h3>
                    </div>
                    <div className={`px-4 py-3 rounded-xl border flex flex-col items-center min-w-[80px] shrink-0 ${getScoreColor(item.averageScore, item.evaluationCount)}`}>
                      <span className="text-2xl font-bold leading-none mb-1">
                        {item.evaluationCount > 0 ? item.averageScore.toFixed(1) : '-'}
                      </span>
                      <span className="text-[10px] uppercase font-bold opacity-80">
                        Ø Score
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-brand-primary/5 p-6 rounded-b-xl">
                  <h4 className="text-sm font-bold text-brand-dark mb-4 flex items-center uppercase tracking-wide">
                    <SafeIcon icon={FiInfo} className="mr-2 text-brand-secondary" />
                    Handlungsempfehlungen
                  </h4>
                  
                  {item.recommendations.length > 0 ? (
                    <div className="grid gap-4">
                      {item.recommendations.map((rec) => (
                        <RecommendationItem 
                          key={rec.id} 
                          rec={rec} 
                          isTeamOwner={isTeamOwner} 
                          onStatusChange={handleStatusChange} 
                          searchTerm={searchTerm}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 italic flex items-center bg-white p-4 rounded-lg border border-gray-200 border-dashed">
                      <SafeIcon icon={FiAlertCircle} className="mr-2" />
                      Für diese Frage wurden noch keine spezifischen Handlungsempfehlungen hinterlegt.
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Handbook;